import axiosNest from '../../api/axiosNest';

/**
 * Zapytania do przewoźników (I.10) — szkic do ręcznej wysyłki.
 * Odpowiada `apps/api/src/carrier-inquiries/carrier-inquiries.controller.ts`.
 * Żadne z tych wywołań niczego nie wysyła do przewoźnika.
 */

export type InquiryLanguage = 'pl' | 'en' | 'de';

export interface InquiryPerson {
  id: number;
  username: string;
}

export interface CarrierInquiry {
  id: number;
  userOfferId: number;
  status: 'draft' | 'sent_manually' | string;
  language: InquiryLanguage | string;
  maskingMode: string;
  maskingFrom: string;
  subject: string;
  body: string;
  generatedBody: string;
  omitted: string[];
  /** Identyfikator standardowego wzoru, z którego powstała treść. */
  template: string;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  subcontractorRule: string | null;
  subcontractorBy: string | null;
  sentReason: string | null;
  carrier: { id: number; name: string; active: boolean | null };
  contact: {
    id: number;
    name: string | null;
    role: string | null;
    channel: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  /**
   * Adresat z kontaktów TMS (16.10.2026, pyt. 56) — kopia z chwili szkicu;
   * starsza odpowiedź API tych pól nie ma.
   */
  tmsRecipientName?: string | null;
  tmsRecipientEmail?: string | null;
  tmsRecipientPhone?: string | null;
  tmsRecipientCode?: string | null;
  createdBy: InquiryPerson | null;
  updatedBy: InquiryPerson | null;
  sentBy: InquiryPerson | null;
  /**
   * Praca dwutorowa (odpowiedź Marathonu 16.10.2026, pyt. 55; przełącznik
   * `tradeDualTrack`): zlecenie w Trade obsadziła flota — zapytanie jest
   * nieaktualne. API dokłada pola tylko wtedy; bez przełącznika ich nie ma.
   */
  outdated?: boolean;
  outdatedReason?: string;
}

export interface InquiryContext {
  allowed: boolean;
  refusal: string | null;
  masking: { companyMode: string | null; requiresChoice: boolean };
  subcontractor: {
    regula: string;
    requiresConfirmation: boolean;
    requiresException: boolean;
    contractor: { id: number; legalName: string } | null;
    mayGrantException: boolean;
    /** Typ klienta z TMS zdaniem (16.10.2026, pyt. 38) — podpowiedź, nie reguła. */
    customerTypeHint?: string | null;
  };
  languages: InquiryLanguage[];
  /** Jeden wzór dla wszystkich przewoźników; `simulated` do czasu wzoru od Marathonu. */
  template: { id: string; simulated: boolean };
}

/**
 * Początek treści symulowanej (`carrier-inquiries/inquiry-template.ts`).
 * Ekran sprawdza samą treść, nie wzór: liczy się, co trader faktycznie wyśle.
 */
export const SIMULATION_MARKER = 'Symulacja treści:';

export const isSimulatedBody = (body: string) => body.trimStart().startsWith(SIMULATION_MARKER);

/**
 * Znacznik podpisu do uzupełnienia (`SIGNATURE_NAME_PLACEHOLDER` w API,
 * 07.10.2026): konto bez imienia i nazwiska (pola konta, decyzja
 * użytkownika 07.10.2026), a login to nie podpis — z wpisanymi API
 * podpisuje samo i znacznika w treści nie ma.
 * Ekran ostrzega, dopóki znacznik jest w treści — trader mógł go już
 * zastąpić, więc liczy się treść, nie wzór.
 */
export const SIGNATURE_NAME_PLACEHOLDER = '[imię i nazwisko]';

export const hasSignaturePlaceholder = (body: string) => body.includes(SIGNATURE_NAME_PLACEHOLDER);

export interface InquiryList {
  context: InquiryContext;
  inquiries: CarrierInquiry[];
}

export interface CreateInquiryInput {
  carrierId: number;
  contactId?: number | null;
  /** Kontakt z TMS (`tmsContacts` przewoźnika) — zamiast `contactId`. */
  tmsContactId?: number | null;
  language?: InquiryLanguage;
  maskingMode?: string;
}

export interface ConfirmSentInput {
  eligibilityConfirmed?: boolean;
  banException?: boolean;
  reason?: string;
}

/** Tryby maskowania, które wolno wybrać (bez dokładnego adresu — `postings/gates.ts`). */
export const MASKING_CHOICES: Array<{ value: string; label: string }> = [
  { value: 'kod-skrocony', label: 'skrócony kod pocztowy (miasto i dwie pierwsze cyfry kodu)' },
  { value: 'tylko-miasto', label: 'tylko miasto (bez kodu pocztowego)' },
];

/**
 * Krótka nazwa trybu przy zapisanym zapytaniu — kod („kod-skrocony”) zostaje
 * w danych, na ekranie nazwa (01.10.2026). Nieznany kod pokazujemy, jak przyszedł.
 */
const MASKING_SHORT_LABELS: Record<string, string> = {
  'kod-skrocony': 'skrócony kod pocztowy',
  'tylko-miasto': 'tylko miasto',
  dokladna: 'pełny adres',
};
export const maskingShortLabel = (mode: string) => MASKING_SHORT_LABELS[mode] ?? mode;

/**
 * Nazwy wzorów zapytania — te same co w API (`carrier-inquiries/inquiry-template.ts`).
 * Identyfikator zostaje w zapytaniu; nieznany pokazujemy z dopiskiem „wzór”.
 */
const TEMPLATE_LABELS: Record<string, string> = {
  'propozycja-2026-09-30': 'wzór tymczasowy z 30.09.2026',
  'symulacja-2026-09-29': 'wzór symulowany z 29.09.2026',
};
export const templateLabel = (id: string) => TEMPLATE_LABELS[id] ?? `wzór ${id}`;

export const LANGUAGE_LABELS: Record<InquiryLanguage, string> = {
  pl: 'polski',
  en: 'angielski',
  de: 'niemiecki',
};

const base = (userOfferId: number | string) => `/user-offers/${userOfferId}/carrier-inquiries`;

export async function fetchInquiries(userOfferId: number | string): Promise<InquiryList> {
  const { data } = await axiosNest.get<InquiryList>(base(userOfferId));
  return data;
}

export async function createInquiry(
  userOfferId: number | string,
  input: CreateInquiryInput,
): Promise<CarrierInquiry> {
  const { data } = await axiosNest.post<CarrierInquiry>(base(userOfferId), input);
  return data;
}

export async function updateInquiry(
  userOfferId: number | string,
  inquiryId: number,
  input: { subject?: string; body?: string },
): Promise<CarrierInquiry> {
  const { data } = await axiosNest.patch<CarrierInquiry>(
    `${base(userOfferId)}/${inquiryId}`,
    input,
  );
  return data;
}

export async function confirmInquirySent(
  userOfferId: number | string,
  inquiryId: number,
  input: ConfirmSentInput,
): Promise<CarrierInquiry> {
  const { data } = await axiosNest.post<CarrierInquiry>(
    `${base(userOfferId)}/${inquiryId}/confirm-sent`,
    input,
  );
  return data;
}

/** Kartoteka: szukajka po nazwie i mieście (`carriers/filters.ts`, klucz `q`). */
export async function searchCarriers(q: string): Promise<Array<{ id: number; name: string }>> {
  const { data } = await axiosNest.get<Array<{ id: number; name: string; city?: string | null }>>(
    '/carriers',
    { params: { filter: JSON.stringify({ q }), range: JSON.stringify([0, 19]) } },
  );
  return data.map((c) => ({ id: c.id, name: c.city ? `${c.name} (${c.city})` : c.name }));
}

/** Kontakt przewoźnika z TMS — kształt `tmsContacts` z `GET /carriers/:id` (16.10.2026). */
export interface TmsRecipientContact {
  id: number;
  corrName: string | null;
  email: string | null;
  phone: string | null;
  typeOfActivity: string | null;
}

/**
 * Kontakty przewoźnika do wyboru adresata szkicu: wpisane przez Trade
 * (`contacts`) i z TMS (`tmsContacts`, odpowiedzi Marathonu/Euvic
 * 16.10.2026, pyt. 56). Starsze API bez `tmsContacts` — pusta lista.
 */
export async function fetchCarrierRecipients(carrierId: number): Promise<{
  contacts: NonNullable<CarrierInquiry['contact']>[];
  tmsContacts: TmsRecipientContact[];
}> {
  const { data } = await axiosNest.get<{
    contacts?: NonNullable<CarrierInquiry['contact']>[];
    tmsContacts?: TmsRecipientContact[];
  }>(`/carriers/${carrierId}`);
  return { contacts: data.contacts ?? [], tmsContacts: data.tmsContacts ?? [] };
}

/** Kontakty przewoźnika z kartoteki — do wyboru adresata szkicu. */
export async function fetchCarrierContacts(
  carrierId: number,
): Promise<NonNullable<CarrierInquiry['contact']>[]> {
  return (await fetchCarrierRecipients(carrierId)).contacts;
}

/** Adresat zapisanego szkicu — kontakt z kartoteki albo kopia kontaktu z TMS. */
export interface InquiryRecipient {
  label: string;
  email: string | null;
  phone: string | null;
  fromTms: boolean;
}

const filled = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim() !== '';

export function inquiryRecipient(
  i: Pick<
    CarrierInquiry,
    'contact' | 'tmsRecipientName' | 'tmsRecipientEmail' | 'tmsRecipientPhone' | 'tmsRecipientCode'
  >,
  codeLabel: (code: string) => string | null = () => null,
): InquiryRecipient | null {
  const c = i.contact;
  if (c) {
    return {
      label: [c.name, c.role, c.channel].filter(filled).join(' · ') || `kontakt #${c.id}`,
      email: c.email,
      phone: c.phone,
      fromTms: false,
    };
  }
  if (![i.tmsRecipientName, i.tmsRecipientEmail, i.tmsRecipientPhone].some(filled)) return null;
  const code = filled(i.tmsRecipientCode) ? (codeLabel(i.tmsRecipientCode) ?? i.tmsRecipientCode) : null;
  return {
    label: [i.tmsRecipientName, code].filter(filled).join(' · ') || 'kontakt z TMS',
    email: i.tmsRecipientEmail ?? null,
    phone: i.tmsRecipientPhone ?? null,
    fromTms: true,
  };
}

/**
 * Tekst do schowka: temat i treść razem, bo trader wkleja je do poczty albo
 * komunikatora, a komunikator tematu nie ma.
 */
export function clipboardText(i: Pick<CarrierInquiry, 'subject' | 'body'>): string {
  return `${i.subject}\n\n${i.body}`;
}

/** Podpowiedź przy „Wyślij” (omówienie 01.10.2026; forma neutralna — bez „sam”). */
export const SEND_HINT = 'Otwiera Twój program pocztowy z gotową treścią — wysyłasz samodzielnie.';

/**
 * Najdłuższy odnośnik `mailto:`, który przekazujemy programowi pocztowemu.
 * Część programów (Outlook przez systemowe skojarzenie) obcina albo odrzuca
 * dłuższe — wtedy treść idzie przez schowek. Typowy szkic ma ok. 1300 znaków
 * po zakodowaniu.
 */
export const MAILTO_MAX_LENGTH = 2000;

/** Treść wiadomości, gdy właściwa nie mieści się w odnośniku — dla tradera, nie przewoźnika. */
export const MAILTO_PASTE_NOTE = '[Wklej tutaj treść zapytania — jest w schowku (Ctrl+V)]';

/** Jeden adres e-mail, bez listy i bez nazwy — kształt jak w kartotece przewoźników. */
const SINGLE_EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/**
 * Adresat wiadomości: e-mail kontaktu, gdy to jeden adres; inaczej pusto —
 * trader wpisze go w programie pocztowym. Jeden przewoźnik = jedna wiadomość
 * do jednej osoby: bez wielu adresatów i bez UDW. Adresata nie zgadujemy.
 */
export function mailtoRecipient(contact: { email?: string | null } | null | undefined): string {
  const email = contact?.email?.trim() ?? '';
  return SINGLE_EMAIL.test(email) ? email : '';
}

const buildMailto = (to: string, subject: string, body: string | null) =>
  `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}${
    body === null ? '' : `&body=${encodeURIComponent(body)}`
  }`;

/**
 * Odnośnik `mailto:` dla „Wyślij” — otwiera szkic w programie pocztowym
 * tradera (I.10: „przygotować i otworzyć e-mail”). Wysyła trader, ze swojej
 * skrzynki. Zawsze jest (omówienie 01.10.2026): bez adresu kontaktu adresat
 * zostaje pusty. Tylko temat i treść — żadnych `cc`/`bcc`.
 *
 * Za długa treść nie trafia do odnośnika (`bodyIncluded: false`): wiadomość
 * dostaje temat i notatkę „wklej treść”, a ekran kopiuje treść do schowka.
 * Ucięta treść poszłaby do przewoźnika niepełna — tego nie robimy.
 */
export function mailtoLink(i: {
  subject: string;
  body: string;
  /** Adresat — kontakt z kartoteki albo kontakt z TMS (sam e-mail wystarczy). */
  contact: { email?: string | null } | null | undefined;
}): {
  href: string;
  bodyIncluded: boolean;
} {
  const to = mailtoRecipient(i.contact);
  const full = buildMailto(to, i.subject, i.body);
  if (full.length <= MAILTO_MAX_LENGTH) return { href: full, bodyIncluded: true };
  const withNote = buildMailto(to, i.subject, MAILTO_PASTE_NOTE);
  // Temat ma najwyżej 300 znaków — sam temat zmieści się zawsze.
  return {
    href: withNote.length <= MAILTO_MAX_LENGTH ? withNote : buildMailto(to, i.subject, null),
    bodyIncluded: false,
  };
}

/** Zanim przyjdzie kontekst zapytań — przyciski „Przygotuj zapytanie” czekają. */
export const INQUIRY_CHECKING = 'Trwa sprawdzanie, czy przy tym zleceniu można przygotować zapytanie.';
export const INQUIRY_CHECK_FAILED =
  'Nie udało się sprawdzić, czy przy tym zleceniu można przygotować zapytanie. Odśwież kartę zlecenia.';

/**
 * Czemu przy tym zleceniu nie da się dziś przygotować zapytania — albo
 * `null`, gdy się da. Rozstrzyga serwer (`carrier-inquiries.service.ts`,
 * `refusal`); ekran nie ma własnej kopii reguły, tylko pokazuje powód przy
 * wyszarzonym przycisku zamiast odmowy po kliknięciu.
 */
export function inquiryBlockReason(context: InquiryContext): string | null {
  if (context.allowed) return null;
  return context.refusal ?? 'Przy tym zleceniu nie można dziś przygotować zapytania.';
}
